/** I13 - the migration chain and the walk that applies it.
 *
 * The chain is a contiguous ladder from `MIN_SAVE_FORMAT_VERSION` to
 * `SAVE_FORMAT_VERSION`, one rung per version, in order. `chainIssues()` states
 * that as a check rather than as a convention, and `migration.spec.ts` runs it:
 * bump `CURRENT_FORMAT_VERSION` without adding `migrations/NNNN.ts` and the
 * suite goes red on the same commit. That is the difference between "a missing
 * migration fails CI" and "a missing migration fails review".
 *
 * A rung reads the old tree through `tree.ts` - `readSave`, `mapEntities`,
 * `addFieldToEntities`, `writeSave` - rather than through `GameState`, which by
 * definition describes a shape the old file does not have. That vocabulary is
 * why `0002.ts` is three lines of body: the boilerplate every rung shares lives
 * in one place instead of being copied into each new file.
 *
 * Adding a rung, as 0002 did: write `migrations/NNNN.ts` exporting a `Migration`
 * with `from: N-1, to: N`, append it to `MIGRATIONS`, bump
 * `CURRENT_FORMAT_VERSION` in `state.ts`, and commit `test/fixtures/save-vN.json`
 * written by `saveGame` *without deleting the older ones*. The fixture test walks
 * every committed file, so the old shapes are the corpus that proves the ladder
 * still climbs.
 */

import type { PersistError } from "../persist/types.js";
import { MIN_SAVE_FORMAT_VERSION, SAVE_FORMAT_VERSION, SAVE_KIND, malformedFile } from "../persist/types.js";
import type { Result } from "../result.js";
import { Err, Ok } from "../result.js";
import type { Migration } from "./types.js";
import { migration0001 } from "./0001.js";
import { migration0002 } from "./0002.js";
import { migration0003 } from "./0003.js";
import { migration0004 } from "./0004.js";
import { migration0005 } from "./0005.js";
import { migration0006 } from "./0006.js";
import { migration0007 } from "./0007.js";
import { migration0008 } from "./0008.js";

export type { Migration } from "./types.js";
export * from "./tree.js";

/** In ascending `from` order. Append; never reorder, never rewrite a rung. */
export const MIGRATIONS: readonly Migration[] = [
  migration0001, migration0002, migration0003, migration0004, migration0005,
  migration0006,
  migration0007,
  migration0008,
];

/**
 * Everything wrong with the ladder, as text. Empty means: every version from
 * the oldest supported to the current one has exactly one rung, each rung
 * climbs by one, and the top rung lands on the current version.
 */
export function chainIssues(): string[] {
  const issues: string[] = [];
  let expected = MIN_SAVE_FORMAT_VERSION;
  for (const m of MIGRATIONS) {
    if (m.from !== expected) {
      issues.push(`migration ladder: expected a rung from ${String(expected)}, found ${String(m.from)}`);
    }
    if (m.to !== m.from + 1) {
      issues.push(`migration ${String(m.from)}->${String(m.to)} skips a version; one rung per step`);
    }
    expected = m.to;
  }
  if (expected !== SAVE_FORMAT_VERSION) {
    issues.push(
      `migration ladder stops at ${String(expected)} but the current format is `
      + `${String(SAVE_FORMAT_VERSION)}: add migrations/${String(expected + 1).padStart(4, "0")}.ts (I13)`,
    );
  }
  return issues;
}

/**
 * The version a raw tree claims.
 *
 * The stamp that counts is the envelope's, and the envelope is identified by
 * `kind`. A tree without one is format 0 by definition - a bare state dump
 * carries `formatVersion: 1` inside it, because that is the *state's* version,
 * and reading that as the file's version would declare an unwrapped blob
 * already current and then fail validation with a confusing message.
 */
export function formatVersionOf(raw: unknown): number | null {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  if (o["kind"] !== SAVE_KIND) return 0;
  const v = o["formatVersion"];
  return typeof v === "number" && Number.isInteger(v) ? v : null;
}

export interface MigrationReport {
  readonly from: number;
  readonly to: number;
  readonly applied: readonly string[];
  readonly tree: Record<string, unknown>;
}

/**
 * Walk a raw tree from whatever version it claims to the current one.
 *
 * A version above the current one is `UNSUPPORTED_VERSION`, not a best effort:
 * a newer build's save can hold fields this build would silently drop, and
 * silently dropping half a kingdom is worse than refusing to open it.
 */
export function migrateToCurrent(raw: unknown): Result<MigrationReport, PersistError> {
  const found = formatVersionOf(raw);
  if (found === null) return Err(malformedFile("no readable formatVersion on this file"));
  if (found > SAVE_FORMAT_VERSION) {
    return Err({ kind: "UNSUPPORTED_VERSION", found, supported: SAVE_FORMAT_VERSION });
  }
  if (found < MIN_SAVE_FORMAT_VERSION) {
    return Err({ kind: "MISSING_MIGRATION", from: found, to: MIN_SAVE_FORMAT_VERSION });
  }

  let tree = raw;
  let at = found;
  const applied: string[] = [];
  while (at < SAVE_FORMAT_VERSION) {
    const step = MIGRATIONS.find((m) => m.from === at);
    if (step === undefined) return Err({ kind: "MISSING_MIGRATION", from: at, to: at + 1 });
    const next = step.migrate(tree);
    if (next === null) {
      return Err(malformedFile(`migration ${String(step.from)}->${String(step.to)} could not read this file`));
    }
    applied.push(step.describe);
    tree = next;
    at = step.to;
  }

  if (tree === null || typeof tree !== "object" || Array.isArray(tree)) {
    return Err(malformedFile("a migration produced a non-object"));
  }
  return Ok({ from: found, to: at, applied, tree: tree as Record<string, unknown> });
}
